import React, { useCallback, useMemo, useState } from 'react';
import ReactSelect, { components, } from 'react-select';
import Creatable from 'react-select/creatable';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { cx } from '@emotion/css';
import { Box } from '@mui/material';
import { ix } from '../../../../utils/ix';
import removeKeysWithUndefined from '../../../../utils/removeKeysWithUndefined';
import { Typography, createBranchTheme } from '../../../electrons';
import { SELECT_DROPDOWN_SELECT_ALL_LABEL, SELECT_DROPDOWN_SELECT_ALL_VALUE, } from './constants';
import { makeCustomStyles, useStyles } from './SelectDropdownField.styles';
import { MenuList } from './components/MenuList/MenuList';
import { MultiValueRemove } from './components/MultiValueRemove/MultiValueRemove';
import { Option } from './components/Option/Option';
import { ValueContainer } from './components/ValueContainer/ValueContainer';
import areMultiProps from './helpers/areMultiProps';
const DEFAULT_CONTAINER_WIDTH = '300px';
const REQUIRED_FOOTNOTE = 'This field is required.';
export function SelectDropdownField(props) {
    const { onChange } = props;
    const theme = createBranchTheme();
    const classes = useStyles();
    const [menuOpened, setMenuOpened] = useState(props.defaultOpen);
    const isAsync = !!props.asyncMenu;
    const isMenuOpenedControlled = isAsync;
    const closeMenuOnSelect = !props.disableCloseOnSelect && !isAsync;
    const blurInputOnSelect = closeMenuOnSelect;
    const customWidth = (props.customStyles && props.customStyles.width) || DEFAULT_CONTAINER_WIDTH;
    const DropdownComponent = props.allowCreate ? Creatable : ReactSelect;
    const onMenuOpen = useCallback(() => {
        if (isMenuOpenedControlled) {
            setMenuOpened(true);
        }
    }, [isMenuOpenedControlled]);
    const onMenuClose = useCallback(() => {
        if (isMenuOpenedControlled) {
            setMenuOpened(false);
        }
    }, [isMenuOpenedControlled]);
    const onSave = useCallback((value) => {
        onChange(value);
        setMenuOpened(false);
    }, [onChange]);
    const onCancel = useCallback(() => {
        setMenuOpened(false);
    }, []);
    const options = useMemo(() => {
        if (areMultiProps(props) && props.showSelectAll) {
            return [
                {
                    value: SELECT_DROPDOWN_SELECT_ALL_VALUE,
                    label: SELECT_DROPDOWN_SELECT_ALL_LABEL,
                },
                ...props.options,
            ];
        }
        return props.options;
    }, [props]);
    const OptionElement = props.renderOption
        ? (renderProps) => props.renderOption(renderProps, components.Option)
        : Option;
    const MenuListElement = (menuListProps) => (React.createElement(MenuList, Object.assign({}, menuListProps, { virtualized: !!props.virtualized, virtualizationConfig: props.virtualizationConfig }), menuListProps.children));
    const DropdownIndicator = (indicatorProps) => {
        return (React.createElement(components.DropdownIndicator, Object.assign({}, indicatorProps, { innerProps: {
                onmousedown: props.onMouseDown,
            } })));
    };
    return (React.createElement(Box, { className: props.className, component: "section", width: customWidth, onMouseDown: props.onMouseDown },
        React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "flex-end" },
            props.title && (React.createElement("div", { id: ix(props.id, 'title'), className: cx('select-title', classes.title) }, props.title)),
            props.showClear && (React.createElement("div", { id: ix(props.id, 'clear'), className: cx('select-clear', classes.clear), onClick: props.onClick }, "Clear"))),
        React.createElement(DropdownComponent, { id: props.id, styles: makeCustomStyles(theme, props.warning, props.required, props.isClearable), value: props.value, isDisabled: props.disabled, menuIsOpen: menuOpened, closeMenuOnSelect: closeMenuOnSelect, options: options, isMulti: props.multiple || props.isMulti, hideSelectedOptions: props.hideSelectedOptions, onChange: props.onChange, onMenuOpen: onMenuOpen, onMenuClose: onMenuClose, placeholder: props.placeholder, 
            // Recreate 'components' object on every render, on purpose.
            // The components implicitly use the props from root,
            // so we don't want to memoize them.
            components: removeKeysWithUndefined(Object.assign({ MenuList: MenuListElement, MultiValueRemove, Option: OptionElement, ValueContainer,
                DropdownIndicator }, (props.components || {}))), blurInputOnSelect: blurInputOnSelect, rootProps: props, customProps: {
                onSave,
                onCancel,
            }, isClearable: props.isClearable }),
        props.required && (React.createElement("div", { className: cx('required', classes.errorMessageContainer) },
            React.createElement(InfoOutlinedIcon, { className: classes.infoIcon }),
            React.createElement(Typography, { variant: "body2" }, REQUIRED_FOOTNOTE)))));
}
//# sourceMappingURL=SelectDropdownField.js.map